import { isDefined } from "@clipboard-health/util-ts";
import type { IconType } from "@src/appV2/redesign/system/ui-components";
import type { IntentVariant } from "@src/appV2/redesign/system/ui-theme";
import { isAxiosError } from "axios";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  type ClockOutErrorType,
  type ClockOutGeofenceValidationType,
  type ClockOutValidationResult,
  validateClockOutTimestamp,
} from "../../MissedPunches/utils/validateClockOutTimestamp";
import { getBottomSheetIntent } from "../TimekeepingAdjustmentBottomSheet";
import { useValidateTimekeepingAction } from "./useValidateTimekeepingAction";

export interface UseClockOutTimeValidationParams {
  selectedTime: Date | undefined;
  shiftId: string;
  shiftEndAt: Date;
  workplaceTimezone: string;
  clockInTime?: Date;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  isEnforceGeofenceTimestampsEnabled: boolean;
  preventLateClockOut?: boolean;
  verifiedClockOut?: Date;
  workerClockOut?: Date;
  isGeofenceWorkplace?: boolean;
  isNfcOrMeshWorkplace?: boolean;
  isDirectIntegrationWorkplace?: boolean;
  suppressMissedPunchWarning?: boolean;
}

export interface UseClockOutTimeValidationResult {
  isValid: boolean;
  intent?: IntentVariant;
  intentContent?: ReactNode;
  intentIcon?: IconType;
  errorType?: ClockOutErrorType;
  geofenceValidationType?: ClockOutGeofenceValidationType;
  errorMessage?: string;
  validateOverlap: () => Promise<boolean>;
  isValidatingOverlap: boolean;
}

export function useClockOutTimeValidation(
  params: UseClockOutTimeValidationParams
): UseClockOutTimeValidationResult {
  const {
    selectedTime,
    shiftId,
    shiftEndAt,
    workplaceTimezone,
    clockInTime,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    isEnforceGeofenceTimestampsEnabled,
    preventLateClockOut = false,
    verifiedClockOut,
    workerClockOut,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    isDirectIntegrationWorkplace,
    suppressMissedPunchWarning = false,
  } = params;

  const { mutateAsync: validateTimekeepingAction, isLoading: isValidatingOverlap } =
    useValidateTimekeepingAction();

  const [overlapError, setOverlapError] = useState<string | undefined>(undefined);

  const localValidation: ClockOutValidationResult | undefined = useMemo(() => {
    if (!isDefined(selectedTime) || !isDefined(clockInTime)) {
      return undefined;
    }

    return validateClockOutTimestamp({
      selectedTime,
      clockInTime,
      geofenceDepartureTime,
      latestPlausibleDepartureTime,
      shiftEndAt,
      workerClockOut,
      isGeofenceFacility: isGeofenceWorkplace,
      isNfcOrMeshFacility: isNfcOrMeshWorkplace,
      isDirectIntegrationFacility: isDirectIntegrationWorkplace,
      isEnforceGeofenceTimestampsEnabled,
      preventLateClockOut,
      verifiedClockOut,
      workplaceTimezone,
    });
  }, [
    selectedTime,
    clockInTime,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    shiftEndAt,
    workerClockOut,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    isDirectIntegrationWorkplace,
    isEnforceGeofenceTimestampsEnabled,
    preventLateClockOut,
    verifiedClockOut,
    workplaceTimezone,
  ]);

  // Clear overlap error when the user changes the selected time.
  // Uses the synchronous render-time pattern (React-recommended for storing
  // information from previous renders) to avoid a one-frame stale error flash.
  const selectedTimeMs = selectedTime?.getTime();
  const prevSelectedTimeMsRef = useRef(selectedTimeMs);
  if (prevSelectedTimeMsRef.current !== selectedTimeMs) {
    prevSelectedTimeMsRef.current = selectedTimeMs;
    if (isDefined(overlapError)) {
      setOverlapError(undefined);
    }
  }

  const validateOverlap = useCallback(async (): Promise<boolean> => {
    if (!isDefined(selectedTime) || shiftId === "") {
      return false;
    }

    try {
      await validateTimekeepingAction({
        shiftId,
        editedClockOut: selectedTime.toISOString(),
      });

      setOverlapError(undefined);
      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setOverlapError(
          (error.response.data as { message?: string })?.message ??
            "Clock-out time cannot overlap with another shift."
        );
        return false;
      }

      setOverlapError("Unable to validate overlap — please try again.");
      return false;
    }
  }, [selectedTime, shiftId, validateTimekeepingAction]);

  const intentProps = useMemo((): {
    intent?: IntentVariant;
    intentContent?: ReactNode;
    intentIcon?: IconType;
  } => {
    if (
      isDefined(localValidation) &&
      !localValidation.isValid &&
      isDefined(localValidation.message)
    ) {
      const { intent, intentIcon } = getBottomSheetIntent("error");
      return { intent, intentIcon, intentContent: localValidation.message };
    }

    if (isDefined(overlapError)) {
      const { intent, intentIcon } = getBottomSheetIntent("error");
      return { intent, intentIcon, intentContent: overlapError };
    }

    if (
      !suppressMissedPunchWarning &&
      isDefined(localValidation) &&
      isDefined(localValidation.warningType) &&
      isDefined(localValidation.message)
    ) {
      const { intent, intentIcon } = getBottomSheetIntent("info");
      return { intent, intentIcon, intentContent: localValidation.message };
    }

    return {};
  }, [localValidation, overlapError, suppressMissedPunchWarning]);

  const isValid = isDefined(localValidation)
    ? localValidation.isValid && !isDefined(overlapError)
    : false;

  return {
    isValid,
    ...intentProps,
    errorType: localValidation?.errorType,
    geofenceValidationType: localValidation?.geofenceValidationType,
    errorMessage:
      isDefined(localValidation?.message) && !localValidation.isValid
        ? localValidation.message
        : overlapError,
    validateOverlap,
    isValidatingOverlap,
  };
}
